import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, Check, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  getTechQuestionnaireSchemaFn,
  submitTechQuestionnaireFn,
  type ApplicationUploadReceipt,
} from '@/lib/firebase/functions'
import {
  DynamicFormRenderer,
  type DynamicFormValues,
} from '@/features/applications/components/DynamicFormRenderer'
import { applicationsService } from '@/features/applications/services/applications.service'
import { ParticipantPackageFlow } from '@/features/participantPackages/components/ParticipantPackageFlow'
import {
  humanizeParticipantPackageError,
  participantPackageService,
} from '@/features/participantPackages/services/participantPackage.service'
import type { ParticipantConfirmationContext } from '@/features/participantPackages/types'
import type { ApplicationSection, FieldAnswer } from '@/types'
import { getErrorCode, withErrorCode } from '@/lib/errors'
import {
  requirementDeadline,
  type ParticipantRequirement,
  type Deliverable,
} from '@/types/productionRequirements'

interface TechSchema {
  techApplicationId: string
  techApplicationTitle: string
  techApplicationDescription: string
  formId: string
  sections: ApplicationSection[]
  alreadySubmitted: boolean
  answers: Record<string, FieldAnswer>
  revision: number
  requirements: ParticipantRequirement[]
  requirementVersion: number
  deliverables: Record<string, Deliverable>
  intakeSummary: Array<{ label: string; value: unknown }>
}

/**
 * Public landing page reached from the acceptance email's secure link.
 *
 * Participant package acceptance/payment is kept in its own feature boundary;
 * the existing technical questionnaire remains below it and is still loaded
 * only after the participant has accepted.
 */
export function ProductionConfirmPage() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const submissionId = searchParams.get('ref') ?? ''
  const token = searchParams.get('token') ?? ''

  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [context, setContext] = useState<ParticipantConfirmationContext | null>(null)
  const [techSchema, setTechSchema] = useState<TechSchema | null>(null)
  const [techLoading, setTechLoading] = useState(false)
  const [techError, setTechError] = useState<string | null>(null)

  const loadTechForContext = useCallback(
    async (nextContext: ParticipantConfirmationContext) => {
      if (!slug || !shouldLoadTechQuestionnaire(nextContext)) {
        setTechSchema(null)
        setTechLoading(false)
        setTechError(null)
        return
      }

      setTechLoading(true)
      setTechError(null)
      try {
        const schema = await loadTechSchema(slug, submissionId, token)
        setTechSchema(schema)
      } catch (error) {
        setTechSchema(null)
        setTechError(
          error instanceof Error
            ? humanizeError(error)
            : 'Could not load the technical questionnaire.'
        )
      } finally {
        setTechLoading(false)
      }
    },
    [slug, submissionId, token]
  )

  useEffect(() => {
    if (!slug || !submissionId || !token) {
      setLoadError(
        withErrorCode(
          'This confirmation link is missing required information.',
          'participant-confirmation/invalid-link'
        )
      )
      setIsLoading(false)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const nextContext = await participantPackageService.getConfirmationContext({
          productionSlug: slug,
          submissionId,
          token,
        })
        if (cancelled) return
        setContext(nextContext)
        setIsLoading(false)
        void loadTechForContext(nextContext)
      } catch (error) {
        if (cancelled) return
        setLoadError(humanizeParticipantPackageError(error))
        setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [loadTechForContext, slug, submissionId, token])

  const handleContextChange = useCallback(
    (nextContext: ParticipantConfirmationContext) => {
      setContext(nextContext)
      void loadTechForContext(nextContext)
    },
    [loadTechForContext]
  )

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading your invitation…
        </div>
      </PageFrame>
    )
  }

  if (loadError || !context || !slug) {
    const expired = loadError?.toLowerCase().includes('expired') ?? false
    return (
      <PageFrame>
        <Card>
          <CardContent className="space-y-3 p-8 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-destructive" aria-hidden="true" />
            <h1 className="text-xl font-semibold">
              {expired ? 'Invitation link expired' : 'Link invalid'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {loadError ??
                withErrorCode(
                  'We could not load this confirmation link. Please try again.',
                  'participant-confirmation/load-failed'
                )}
            </p>
            <p className="pt-2 text-xs text-muted-foreground">
              If you believe this is in error, reply to the invitation email you received and
              we&apos;ll sort it out.
            </p>
          </CardContent>
        </Card>
      </PageFrame>
    )
  }

  return (
    <PageFrame>
      {!!context.requirements?.length && (
        <Card>
          <CardContent className="space-y-3 p-6">
            <h2 className="font-semibold">Required materials</h2>
            {context.requirements.map((requirement) => (
              <div key={requirement.id} className="space-y-1 text-sm">
                <p className="font-medium">
                  {requirement.label}
                  {requirement.required ? ' (required)' : ' (optional)'}
                </p>
                <p>Due {requirementDeadline(requirement)}</p>
                <p className="whitespace-pre-line text-muted-foreground">
                  {requirement.instructions}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <ParticipantPackageFlow
        context={context}
        productionSlug={slug}
        submissionId={submissionId}
        token={token}
        onContextChange={handleContextChange}
      />

      {techLoading && (
        <Card>
          <CardContent className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading your technical questionnaire…
          </CardContent>
        </Card>
      )}

      {techError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{techError}</AlertDescription>
        </Alert>
      )}

      {techSchema && (
        <TechFormCard
          key={techSchema.revision}
          ctx={context}
          schema={techSchema}
          slug={slug}
          submissionId={submissionId}
          token={token}
          onSubmitted={() => {
            const nextContext = { ...context, techCompleted: true }
            setContext(nextContext)
            void loadTechForContext(nextContext)
          }}
          onError={setTechError}
        />
      )}
    </PageFrame>
  )
}

function PageFrame({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-3xl items-center px-4 py-12">
      <div className="w-full space-y-5">{children}</div>
    </div>
  )
}

interface TechFormCardProps {
  ctx: ParticipantConfirmationContext
  schema: TechSchema
  slug: string
  submissionId: string
  token: string
  onSubmitted: () => void
  onError: (message: string) => void
}

function TechFormCard({
  ctx,
  schema,
  slug,
  submissionId,
  token,
  onSubmitted,
  onError,
}: TechFormCardProps) {
  const fileUploadsRef = useRef<Record<string, ApplicationUploadReceipt[]>>({})
  const [submitting, setSubmitting] = useState(false)
  const [fileUploadActivity, setFileUploadActivity] = useState<Record<string, boolean>>({})

  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<DynamicFormValues>({
    defaultValues: {
      applicantInfo: { firstName: '', lastName: '', email: '' },
      answers: Object.fromEntries(
        Object.entries(schema.answers).map(([id, answer]) => [id, answer.value])
      ),
      files: {},
    },
  })

  async function handleFileUpload(
    fieldId: string,
    file: File,
    onProgress: (pct: number) => void
  ): Promise<string> {
    const receipt = await applicationsService.uploadFile(
      schema.techApplicationId,
      schema.formId,
      fieldId,
      file,
      onProgress,
      { productionSlug: slug, submissionId, token }
    )
    fileUploadsRef.current[fieldId] = [receipt]
    return receipt.url
  }

  function handleFileRemove(fieldId: string) {
    delete fileUploadsRef.current[fieldId]
  }

  function handleFileStateChange(fieldId: string, uploading: boolean) {
    setFileUploadActivity((current) => ({ ...current, [fieldId]: uploading }))
  }

  async function onSubmit(values: DynamicFormValues) {
    if (Object.values(fileUploadActivity).some(Boolean)) {
      onError('Please wait for your file upload to finish.')
      return
    }
    setSubmitting(true)
    try {
      const answers: Record<string, FieldAnswer> = {}
      for (const section of schema.sections) {
        for (const field of section.fields) {
          const raw = values.answers[field.id]
          if (raw !== undefined && raw !== null) {
            answers[field.id] = {
              label: field.label,
              ...(field.fieldLibraryId ? { fieldLibraryId: field.fieldLibraryId } : {}),
              value: raw,
            }
          }
        }
      }
      await submitTechQuestionnaireFn({
        productionSlug: slug,
        submissionId,
        token,
        answers,
        fileUploads: fileUploadsRef.current,
        expectedRevision: schema.revision,
      })
      onSubmitted()
    } catch (error) {
      onError(
        humanizeError(error, 'We could not submit your technical information. Please try again.')
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-6 p-6 sm:p-8">
        <div className="space-y-1 text-center">
          <p className="inline-flex items-center justify-center rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-700">
            <Check className="mr-1 h-3 w-3" aria-hidden="true" /> Spot accepted —{' '}
            {ctx.productionName}
          </p>
          <h1 className="pt-3 text-2xl font-bold">{schema.techApplicationTitle}</h1>
          {schema.techApplicationDescription && (
            <p className="mx-auto max-w-md text-sm text-muted-foreground">
              {schema.techApplicationDescription}
            </p>
          )}
        </div>

        {!!schema.intakeSummary.length && (
          <dl className="rounded-md border p-4 text-sm space-y-2">
            {schema.intakeSummary.map((answer, index) => (
              <div key={index}>
                <dt className="font-medium">{answer.label}</dt>
                <dd>{String(answer.value ?? '')}</dd>
              </div>
            ))}
          </dl>
        )}
        {schema.requirements.map((requirement) => {
          const delivery = schema.deliverables[requirement.id]
          const current =
            delivery?.requirementVersion === schema.requirementVersion ? delivery : undefined
          const status =
            current?.status === 'verified'
              ? 'Verified — our team has saved your screening copy'
              : current?.status === 'submitted'
                ? 'Submitted — awaiting staff review'
                : current?.status === 'needs_changes'
                  ? 'Changes requested'
                  : 'Awaiting your submission'
          const overdue =
            Date.now() > Date.parse(requirement.dueAt) &&
            (!current || current.status === 'needs_changes')
          return (
            <div key={requirement.id} className="space-y-1 rounded-md border p-4 text-sm">
              <p className="font-medium">
                {requirement.label}: {status}
              </p>
              {current?.reviewNote && (
                <p className="whitespace-pre-line">Staff feedback: {current.reviewNote}</p>
              )}
              {overdue && (
                <p className="text-amber-700">
                  The deadline has passed. You can still submit or correct your materials here for
                  staff review.
                </p>
              )}
            </div>
          )
        })}
        {schema.alreadySubmitted && (
          <div className="rounded-md border bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
            Your answers are saved below. Changing a file link or its access details requires our
            team to verify the updated file again.
          </div>
        )}

        <form onSubmit={(event) => void handleSubmit(onSubmit)(event)} className="space-y-6">
          <DynamicFormRenderer
            sections={schema.sections}
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            onFileUpload={handleFileUpload}
            onFileRemove={handleFileRemove}
            onFileStateChange={handleFileStateChange}
            disabled={submitting}
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting || Object.values(fileUploadActivity).some(Boolean)}
            >
              {submitting && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />}
              {schema.alreadySubmitted ? 'Save updated materials' : 'Submit materials'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

async function loadTechSchema(
  slug: string,
  submissionId: string,
  token: string
): Promise<TechSchema> {
  const result = await getTechQuestionnaireSchemaFn({ productionSlug: slug, submissionId, token })
  return {
    ...result.data,
    sections: result.data.sections as ApplicationSection[],
  }
}

function shouldLoadTechQuestionnaire(context: ParticipantConfirmationContext): boolean {
  if (!context.hasTechQuestionnaire || (context.techCompleted && !context.requirements?.length))
    return false
  if (context.offerStatus) {
    return context.offerStatus === 'accepted_unpaid' || context.offerStatus === 'paid'
  }
  return context.currentResponse === 'accepted'
}

function humanizeError(
  error: unknown,
  fallback = 'We could not load the technical questionnaire. Please try again.'
): string {
  const errorCode = getErrorCode(error, 'participant-confirmation/request-failed')
  const code = errorCode.replace(/^functions\//, '')
  if (code === 'permission-denied') {
    return withErrorCode(
      'This confirmation link is invalid or has been revoked. Please use the latest link from your invitation email.',
      errorCode
    )
  }
  if (code === 'aborted')
    return withErrorCode(
      'This submission changed in another window. Reload to see the latest answers before saving.',
      errorCode
    )
  if (code === 'not-found') {
    return withErrorCode('The invitation referenced by this link no longer exists.', errorCode)
  }
  return withErrorCode(fallback, errorCode)
}
